/**
 * @file api-site-not-found.ts
 * @description Клиент журнала «Сайт → SEO → Не найдено» (phase19.16, партия
 * «SEO-техника», v0.19.65; SB148, SB55; проект данных
 * db-design-seo-tech.md §4.2–§4.3).
 *
 * Журнал — адреса сайта, по которым посетители и поисковики получили 404,
 * суммами за «Сегодня · 7 дней · 30 дней» (по поясу магазина). Статус строки
 * считает сервер при чтении (переадресован, правило не действует, адрес
 * снова открывается). Читать — право контента или настроек; «Скрыть» и
 * «Вернуть в список» — право настроек. Путь в «Вернуть» уходит строкой
 * запроса (`?path=`), а не частью адреса: в пути есть «/».
 *
 * DTO — из `@chaiberi/shared` (`site-seo/not-found-filter.ts`), общие с API.
 * После «Скрыть»/«Вернуть» журнал перечитывается, а не правится руками.
 */

import type { NotFoundJournalDto, NotFoundPeriod, NotFoundSort } from "@chaiberi/shared";

import { apiFetch } from "./api-client";

const BASE = "/admin/site/not-found";

/** Строк на страницу («Показать ещё» — следующие 50). */
export const NOT_FOUND_PAGE = 50;

export interface NotFoundQuery {
  period: NotFoundPeriod;
  /** Показать и обработанные, и скрытые (фильтр «Обработанные и скрытые»). */
  all: boolean;
  q?: string;
  sort: NotFoundSort;
  offset?: number;
  limit?: number;
}

/** Строка запроса журнала: пустой поиск и нулевой сдвиг не отправляются. */
export function notFoundQueryString(query: NotFoundQuery): string {
  const q = new URLSearchParams({ period: query.period, all: query.all ? "1" : "0", sort: query.sort, limit: String(query.limit ?? NOT_FOUND_PAGE) });
  const search = query.q?.trim();
  if (search) q.set("q", search);
  if (query.offset) q.set("offset", String(query.offset));
  return q.toString();
}

export const siteNotFound = {
  /** Журнал за период: строки, всего после фильтра, «прочие», счётчики фильтра, начало журнала. */
  list: (query: NotFoundQuery): Promise<NotFoundJournalDto> => apiFetch<NotFoundJournalDto>(`${BASE}?${notFoundQueryString(query)}`),

  /** Скрыть адрес (мусор ботов); повтор — без ошибки. Предел 5000 — 409 `site_not_found_hidden_limit`. */
  hide: (path: string): Promise<unknown> => apiFetch<unknown>(`${BASE}/hide`, { method: "POST", json: { path } }),

  /** Вернуть скрытый адрес в список. */
  unhide: (path: string): Promise<unknown> => apiFetch<unknown>(`${BASE}/hide?path=${encodeURIComponent(path)}`, { method: "DELETE" }),
};
